import "./App.css";
import React, { useEffect } from "react";

import { RouterProvider, createRouter } from "@tanstack/react-router";
import { QueryClient } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";

import {
  CACHE_BUSTER,
  createTrackerQueryPersister,
  shouldPersistQuery,
} from "@/lib/queryPersister";
import { subscribeInvalidate } from "@/lib/crossTabBroadcast";
import { initSentry } from "@/lib/sentry";

// Sentry must be initialized BEFORE any component that could throw is
// mounted — including the ``ChatErrorBoundary`` we wrap tool cards in.
// Module-load time is the earliest hook the FE has; the first import
// of this file (via ``src/main.tsx``) runs this before React renders.
// Silent no-op when ``VITE_SENTRY_DSN`` is unset (local dev / tests).
initSentry();

// Import the generated route tree
import { routeTree } from "./routeTree.gen";

// Create a new router instance
const router = createRouter({
  routeTree,
  // defaultPreload: 'intent',
  // Scroll restoration: scroll to top on a NEW navigation (so e.g.
  // building-detail → Dashboard lands at the top, not wherever the
  // previous page was scrolled) and restore the prior offset on
  // back/forward. The portal layout scrolls the window (no inner scroll
  // container), so the default window-scroll behavior is correct.
  scrollRestoration: true,
  context: { auth: undefined! },
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15 * 60 * 1000,
      // gcTime must exceed the longest persisted staleTime + a buffer,
      // otherwise queries get garbage-collected from the in-memory
      // cache BEFORE the persister can dehydrate them. 7d is the
      // tracker plan's `maxAge` for the persisted entries too.
      gcTime: 7 * 24 * 60 * 60 * 1000,
      // Stale-WHILE-REVALIDATE, not stale-FOREVER. The persister still
      // paints the cached entry instantly on mount (no spinner), but a
      // STALE entry (older than staleTime) must refetch in the
      // background so the number reconciles. The prior `false` trio
      // disabled the revalidate half entirely: a browser froze on its
      // first fetch and never refreshed through navigation/focus/
      // reconnect for up to the 7-day persistence window — so two
      // browsers (or two tabs) showed different numbers indefinitely,
      // and clearing one browser's localStorage didn't reconcile the
      // other. These only fire when the entry is already stale, so
      // instant-paint is preserved and refetch traffic stays bounded.
      refetchOnWindowFocus: true,
      refetchOnMount: true,
      refetchOnReconnect: true,
    },
  },
});

const persister = createTrackerQueryPersister();

const InnerApp = () => {
  // Phase 2B: subscribe THIS tab's QueryClient to invalidation
  // broadcasts from sibling same-origin tabs. Mounting at app boot
  // (single subscription per tab lifetime) matches the channel's
  // intended lifecycle. The returned ``unsubscribe`` runs only on
  // unmount, which in practice means tab close — but we honor it so
  // hot-reloads in dev don't leak open channels.
  useEffect(() => {
    return subscribeInvalidate(queryClient);
  }, []);
  return <RouterProvider router={router} />;
};

export const App = () => (
  <PersistQueryClientProvider
    client={queryClient}
    persistOptions={{
      persister,
      // Discard persisted entries older than 7d on hydration.
      maxAge: 7 * 24 * 60 * 60 * 1000,
      // A new build sha (or CACHE_SCHEMA_VERSION) drops every prior entry.
      buster: CACHE_BUSTER,
      dehydrateOptions: {
        shouldDehydrateQuery: shouldPersistQuery,
      },
    }}
  >
    <InnerApp />
  </PersistQueryClientProvider>
);

export default App;
